import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Activity, Package, Loader2, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ActivityLast24Sidebar — compact "past 24 hours" section for the
 * left navigation sidebar.  Lives at the bottom, below the tool list
 * (2026-02-08 user request).  Lists the most recent app upgrades so
 * the owner can glance at what shipped without navigating anywhere.
 * Full drill-down at /activity.
 */
export default function ActivityLast24Sidebar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/activity-log/last-24h`, { timeout: 12000 });
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        // Silent — sidebar is not critical if the endpoint hiccups.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Poll every 3 minutes so the sidebar keeps up with new deploys.
    const t = setInterval(load, 180000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Prefer upgrades since that's the "what shipped" the user cares
  // about most on the sidebar; fall back to user actions if none.
  const upgrades = items.filter((it) => it.category === 'system_upgrades').slice(0, 5);
  const actions = items.filter((it) => it.category !== 'system_upgrades').slice(0, 3);

  if (loading) {
    return (
      <div className="mt-4 pt-4 border-t border-zinc-800 px-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold flex items-center gap-1.5 mb-2">
          <Activity className="w-3 h-3" /> Past 24 Hours
        </div>
        <div className="flex items-center gap-1.5 text-xs text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-zinc-800 px-1" data-testid="sidebar-activity-last24">
      <Link
        to="/activity"
        className="group flex items-center justify-between mb-2 hover:text-emerald-300 transition"
      >
        <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-bold flex items-center gap-1.5">
          <Activity className="w-3 h-3" /> Past 24 Hours
        </div>
        <ChevronRight className="w-3 h-3 text-zinc-500 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition" />
      </Link>

      {upgrades.length > 0 && (
        <div className="space-y-1 mb-2">
          <div className="text-[9px] uppercase tracking-wider text-violet-400 font-bold flex items-center gap-1">
            <Package className="w-2.5 h-2.5" /> App Upgrades · {upgrades.length}
          </div>
          {upgrades.map((it) => (
            <div
              key={it.id}
              className="text-[10.5px] leading-snug text-zinc-400 hover:text-zinc-200 pl-1 truncate"
              title={it.subject_label || it.description}
              data-testid={`sidebar-upgrade-${it.event_type}`}
            >
              <span className="text-violet-500 mr-0.5">•</span>
              {(it.subject_label || it.description).replace(/^App upgrade shipped:\s*/, '')}
            </div>
          ))}
        </div>
      )}

      {actions.length > 0 && (
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold">
            User Actions · {actions.length}
          </div>
          {actions.map((it) => (
            <div
              key={it.id}
              className="text-[10.5px] leading-snug text-zinc-400 hover:text-zinc-200 pl-1 truncate"
              title={it.description}
            >
              <span className="text-emerald-500 mr-0.5">•</span>
              {it.description}
            </div>
          ))}
        </div>
      )}

      {upgrades.length === 0 && actions.length === 0 && (
        <div className="text-[10.5px] italic text-zinc-500">
          Nothing yet in the last 24 hours.
        </div>
      )}
    </div>
  );
}
